import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'widget-isi-haritasi',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (sutunEtiketleri().length) {
      <div class="ih-grid" [style.grid-template-columns]="gridKolonlar()">
        <div class="ih-kose"></div>
        @for (s of sutunEtiketleri(); track s) {
          <div class="ih-sutun-etiket">{{ s }}</div>
        }
        @for (satir of hucreler(); track satir.idx) {
          <div class="ih-satir-etiket">{{ satir.etiket }}</div>
          @for (h of satir.hucreler; track h.idx) {
            <div
              class="ih-hucre"
              [style.background-color]="h.renk"
              [style.color]="h.yaziRenk"
              [title]="h.deger"
            >{{ h.metin }}</div>
          }
        }
      </div>
    }
  `,
  styles: [`
    :host { display: block; overflow: auto; }
    .ih-grid {
      display: grid;
      gap: 2px;
      width: 100%;
    }
    .ih-kose { min-width: 40px; }
    .ih-sutun-etiket {
      font-size: 10px;
      text-align: center;
      opacity: 0.65;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding: 2px 1px;
    }
    .ih-satir-etiket {
      font-size: 10px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding-right: 4px;
      opacity: 0.65;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .ih-hucre {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 500;
      border-radius: 3px;
      aspect-ratio: 1;
      min-width: 20px;
      transition: transform 0.2s cubic-bezier(0.4,0,0.2,1), box-shadow 0.2s;
      cursor: default;
      user-select: none;
    }
    .ih-hucre:hover {
      transform: scale(1.15);
      box-shadow: 0 2px 8px rgba(0,0,0,0.25);
      z-index: 1;
      position: relative;
    }
  `],
})
export class IsiHaritasiComponent {
  veriler = input<number[][]>([]);
  satirEtiketleri = input<string[]>([]);
  sutunEtiketleri = input<string[]>([]);
  minRenk = input('#e6f7ff');
  maxRenk = input('#0050b3');

  private aralik = computed(() => {
    const v = this.veriler();
    if (!v?.length) return { min: 0, max: 1 };
    let min = Infinity, max = -Infinity;
    for (let i = 0; i < v.length; i++) {
      for (let j = 0; j < v[i].length; j++) {
        const d = v[i][j];
        if (d < min) min = d;
        if (d > max) max = d;
      }
    }
    if (min === max) return { min: min - 1, max: max + 1 };
    return { min, max };
  });

  gridKolonlar = computed(() => {
    const s = this.sutunEtiketleri();
    return `minmax(40px, auto) repeat(${s.length}, 1fr)`;
  });

  hucreler = computed(() => {
    const v = this.veriler();
    const sEtiketler = this.satirEtiketleri();
    const { min, max } = this.aralik();
    const minRgb = this.hexToRgb(this.minRenk());
    const maxRgb = this.hexToRgb(this.maxRenk());
    const fark = max - min;

    return v.map((satir, si) => ({
      idx: si,
      etiket: sEtiketler[si] ?? '',
      hucreler: satir.map((deger, hi) => {
        const t = fark ? (deger - min) / fark : 0;
        const r = Math.round(minRgb.r + (maxRgb.r - minRgb.r) * t);
        const g = Math.round(minRgb.g + (maxRgb.g - minRgb.g) * t);
        const b = Math.round(minRgb.b + (maxRgb.b - minRgb.b) * t);
        const parlaklik = (r * 299 + g * 587 + b * 114) / 1000;
        return {
          idx: hi,
          deger,
          metin: Number.isInteger(deger) ? String(deger) : deger.toFixed(1),
          renk: `rgb(${r},${g},${b})`,
          yaziRenk: parlaklik > 128 ? '#000000cc' : '#ffffffdd',
        };
      }),
    }));
  });

  private hexToRgb(hex: string) {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return { r: isNaN(r) ? 0 : r, g: isNaN(g) ? 0 : g, b: isNaN(b) ? 0 : b };
  }
}
